import { useRef, useState, useEffect, memo, useMemo } from "react";
import { Box, TableCell, TableRow, Typography, Tooltip } from "@mui/material";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import WarningIcon from '@mui/icons-material/Warning';
import ContextMenu from './ContextMenu';

const MarketWatchRow = ({
  symbol,
  index,
  bid,
  ask,
  digits,
  isMockData = false,
  onSymbolInfo,
  onSymbolSelect
}) =>{
  const [prevBid, setPrevBid] = useState(bid);
  const [prevAsk, setPrevAsk] = useState(ask);
  const [bidDirection, setBidDirection] = useState('');
  const [askDirection, setAskDirection] = useState('');
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const rowRef = useRef(null);
  
  // Track price changes
  useEffect(() => {
    if (prevBid !== bid) {
      setBidDirection(bid > prevBid ? 'up' : 'down');
      setPrevBid(bid);
      setTimeout(() => setBidDirection(''), 500);
    }
  }, [bid, prevBid]);
  
  useEffect(() => {
    if (prevAsk !== ask) {
      setAskDirection(ask > prevAsk ? 'up' : 'down');
      setPrevAsk(ask);
      setTimeout(() => setAskDirection(''), 500);
    }
  }, [ask, prevAsk]);
  
  const point = digits > 0 
    ? 1 / Math.pow(10, digits)
    : (0.1).toFixed(digits) ;

  const handleContextMenu = (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    // Use the ref to get the actual DOM element
    const domElement = rowRef.current;
    
    if (domElement instanceof HTMLElement) {
      setAnchorEl(domElement);
    } else {
      setAnchorEl(event.currentTarget);
    }
    
    setContextMenuOpen(true);
    
    // Close any open Symbol Info modal when right-clicking on a different symbol
    if (onSymbolInfo) {
      onSymbolInfo(null, null); // This will close the modal
    }
  };

  const handleCloseContextMenu = () => {
    setContextMenuOpen(false);
    setAnchorEl(null);
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenuOpen && !event.target.closest('.MuiMenu-root')) {
        handleCloseContextMenu();
      }
    };

    if (contextMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenuOpen]);

  const handleSymbolInfo = (symbolName, passedAnchorEl) => {
    if (onSymbolInfo) {
      onSymbolInfo(symbolName, passedAnchorEl || anchorEl);
    }
  };

  const handleSymbolClick = (event) => {
    event.stopPropagation();
    if (onSymbolSelect) {
      // Create a symbol object that matches the API structure
      const symbolData = {
        id: null, // We don't have ID from tick data, will be resolved in components
        name: symbol,
        lpSymbolName: symbol, // Use symbol name as lpSymbolName for now
        isActive: true
      };
      onSymbolSelect(symbolData);
    }
  };
    
    return (
      <>
        <TableRow
          ref={rowRef}
          onContextMenu={handleContextMenu}
          sx={{
            "& .MuiTableCell-root": {
              borderBottom: "none",
              px: { xs: 0.25, sm: 0.5, md: 0.5 },
              py: { xs: 0.5, sm: 1, md: 1.5 },
              cursor: "pointer",
              fontSize: { xs: '9px', sm: '10px', md: '11px' }
            },
            backgroundColor: isMockData ? "rgba(245, 158, 11, 0.1)" : "primary.main",
            borderRadius: "4px",
            "& td:first-of-type": {
              borderTopLeftRadius: "6px",
              borderBottomLeftRadius: "6px",
              borderTopRightRadius:  "",
              borderBottomRightRadius:  "",
            },
            "& td:last-child": {
              borderTopRightRadius: "6px",
              borderBottomRightRadius: "6px",
              borderTopLeftRadius: "",
              borderBottomLeftRadius: "",
            },
            "&:hover": {
              backgroundColor: isMockData ? "rgba(245, 158, 11, 0.2)" : "tertiary.main",
            },
            ...(isMockData && {
              border: "1px solid rgba(245, 158, 11, 0.3)",
            })
          }}
        >
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {bidDirection === 'up' && (
                <ArrowUpwardIcon sx={{ 
                  color: '#22c55e', 
                  fontSize: { xs: 12, sm: 14, md: 16 }, 
                  verticalAlign: 'middle' 
                }} />
              )}
              {bidDirection === 'down' && (
                <ArrowDownwardIcon sx={{ 
                  color: '#ef4444', 
                  fontSize: { xs: 12, sm: 14, md: 16 }, 
                  verticalAlign: 'middle' 
                }} />
              )}
              {!bidDirection && (
                <ArrowUpwardIcon sx={{ 
                  color: '#334155', 
                  fontSize: { xs: 12, sm: 14, md: 16 }, 
                  opacity: 0.5, 
                  verticalAlign: 'middle' 
                }} />
              )}
              <Typography 
                className="market-watch-symbol"
                onClick={handleSymbolClick}
                sx={{
                  cursor: 'pointer',
                  color: '#06b6d4',
                  fontWeight: 600,
                  fontSize: { xs: '9px', sm: '10px', md: '11px' },
                  '&:hover': {
                    color: '#0891b2',
                    textDecoration: 'underline'
                  }
                }}
              >
                {symbol}
              </Typography>
              {isMockData && (
                <Tooltip title="Cached data - not live">
                  <WarningIcon sx={{ 
                    color: '#f59e0b', 
                    fontSize: { xs: 10, sm: 12, md: 14 }, 
                    opacity: 0.8,
                    cursor: 'help'
                  }} />
                </Tooltip>
              )}
            </Box>
          </TableCell>
        <TableCell align="right" sx={{
          minWidth: { xs: "50px", sm: "55px", md: "60px" },
          maxWidth: { xs: "50px", sm: "55px", md: "60px" }
        }}>
              <Typography
                className={`market-watch-bid bid-price ${bidDirection ? `price-${bidDirection}` : ''}`}
                sx={{
                  fontSize: { xs: '9px', sm: '10px', md: '11px' },
                  fontWeight: { xs: 500, sm: 600, md: 600 },
                  ...(isMockData && {
                    opacity: 0.7,
                    fontStyle: 'italic'
                  })
                }}
              >
                {bid?.toFixed(digits)}
              </Typography>
        </TableCell>
        <TableCell align="right" sx={{
          minWidth: { xs: "50px", sm: "55px", md: "60px" },
          maxWidth: { xs: "50px", sm: "55px", md: "60px" }
        }}>
              <Typography
                className={`market-watch-ask ask-price ${askDirection ? `price-${askDirection}` : ''}`}
                sx={{
                  fontSize: { xs: '9px', sm: '10px', md: '11px' },
                  fontWeight: { xs: 500, sm: 600, md: 600 },
                  ...(isMockData && {
                    opacity: 0.7,
                    fontStyle: 'italic'
                  })
                }}
              >
                {ask?.toFixed(digits)}
              </Typography>
          </TableCell>
          <TableCell align="right" sx={{
            display: { xs: 'none', sm: 'table-cell' },
            minWidth: { xs: "45px", sm: "50px", md: "55px" },
            maxWidth: { xs: "45px", sm: "50px", md: "55px" }
          }}>
            <Typography
              className="market-watch-spread"
              sx={{
                fontSize: { xs: '8px', sm: '9px', md: '10px' },
                fontWeight: { xs: 400, sm: 500, md: 500 },
                ...(isMockData && {
                  opacity: 0.7,
                  fontStyle: 'italic'
                })
              }}
            >
              {((ask-bid)/point)?.toFixed(0)}
            </Typography>
          </TableCell>
          <TableCell align="right" sx={{
            display: { xs: 'none', sm: 'table-cell' },
            minWidth: { xs: "40px", sm: "45px", md: "50px" },
            maxWidth: { xs: "40px", sm: "45px", md: "50px" }
          }}>
            <Typography
              className="market-watch-digits"
              sx={{
                fontSize: { xs: '8px', sm: '9px', md: '10px' },
                fontWeight: { xs: 400, sm: 500, md: 500 }
              }}
            >
              {digits}
            </Typography>
          </TableCell>  
        </TableRow>
        
        <ContextMenu
          anchorEl={anchorEl}
          open={contextMenuOpen}
          onClose={handleCloseContextMenu}
          onSymbolInfo={handleSymbolInfo}
          symbolName={symbol}
        />
      </>
    )
};

export default memo(MarketWatchRow);